
import React, { useState, useEffect } from 'react';
import cambioEstadoService from "../../services/cambioEstadoService";
import { obtenerEstadoARTPorNombre } from "../../services/art/artEstadoService";
import { toast } from "react-toastify";
import EventoService from "../../services/eventoService";
import { listarMedicos } from "../../services/medicoService";
import DatosMaestrosService from "../../services/datosMaestrosService";
import ComboboxDatosMaestros from "../../components/ComboboxDatosMaestros";

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const formatLocalFecha = (fechaInput) => {
    if (!fechaInput) return '';
    if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) {
        return fechaInput;
    }
    const d = new Date(fechaInput);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

const parseLocalDate = (fechaInput) => {
    const fechaISO = formatLocalFecha(fechaInput);
    if (!fechaISO) return null;
    return new Date(`${fechaISO}T00:00:00`);
};

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => formatLocalFecha(new Date());

// Función auxiliar para calcular hora fin
const calcularHoraFin = (horaInicio, minutos) => {
    const [horas, mins] = horaInicio.split(':').map(Number);
    const totalMinutos = horas * 60 + mins + minutos;
    const nuevasHoras = Math.floor(totalMinutos / 60) % 24;
    const nuevosMinutos = totalMinutos % 60;
    return `${String(nuevasHoras).padStart(2, '0')}:${String(nuevosMinutos).padStart(2, '0')}`;
};

// Función auxiliar para formatear fecha
const formatearFecha = (fechaStr) => {
    const fecha = parseLocalDate(fechaStr);
    if (!fecha) return 'Fecha no disponible';
    return fecha.toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

// --- COMPONENTE REUTILIZABLE PARA EL FORMULARIO DE TURNO ---
const FormularioTurnoART = ({ visible, onConfirmar, onCancelar, cargando, medicos, cargandoMedicos, lugares = [] }) => {
    const [datosTurno, setDatosTurno] = useState({
        fecha: obtenerFechaActual(),
        hora: '',
        idMedico: '',
        lugar: '',
        observaciones: ''
    });

    useEffect(() => {
        // Resetea el formulario cuando se oculta
        if (!visible) {
            setDatosTurno({ fecha: obtenerFechaActual(), hora: '', idMedico: '', lugar: '', observaciones: '' });
        }
    }, [visible]);

    if (!visible) return null;

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <h5 className="fw-semibold text-secondary mb-3">Asignar Turno Médico</h5>
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Fecha</label>
                    <div className="input-group">
                        <span className="input-group-text bg-light"><CalendarMonthIcon fontSize="small" /></span>
                        <input type="date" className="form-control shadow-sm" value={datosTurno.fecha} onChange={(e) => setDatosTurno({ ...datosTurno, fecha: e.target.value })} required />
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Hora</label>
                    <div className="input-group">
                        <span className="input-group-text bg-light"><AccessTimeIcon fontSize="small" /></span>
                        <input type="time" className="form-control shadow-sm" value={datosTurno.hora} onChange={(e) => setDatosTurno({ ...datosTurno, hora: e.target.value })} required />
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Médico</label>
                    <div className="input-group">
                        <span className="input-group-text bg-light"><PersonIcon fontSize="small" /></span>
                        <select className="form-select shadow-sm" value={datosTurno.idMedico} onChange={(e) => setDatosTurno({ ...datosTurno, idMedico: e.target.value })} disabled={cargandoMedicos} required>
                            <option value="">Seleccionar médico...</option>
                            {medicos.map(medico => (
                                <option key={medico.idMedico} value={medico.idMedico}>
                                    {medico.nombre} {medico.apellido} {medico.matricula ? `(Mat: ${medico.matricula})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    {cargandoMedicos && <small className="text-muted">Cargando médicos...</small>}
                </div>
                <div className="col-md-6">
                    <ComboboxDatosMaestros
                        opciones={lugares.filter(l => l.tipo === 'Consultorio' || !l.tipo)}
                        valor={datosTurno.lugar}
                        onChange={(valor) => setDatosTurno({ ...datosTurno, lugar: valor })}
                        tipo="lugar"
                        tipoLugar="Consultorio"
                        placeholder="Seleccione o escriba un lugar..."
                        label="Lugar"
                        icon={PlaceIcon}
                    />
                </div>
                <div className="col-12">
                    <label className="form-label fw-semibold">Observaciones</label>
                    <textarea className="form-control shadow-sm" rows="3" placeholder="Indicaciones especiales, preparación necesaria, etc." value={datosTurno.observaciones} onChange={(e) => setDatosTurno({ ...datosTurno, observaciones: e.target.value })} />
                </div>
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={() => onConfirmar(datosTurno)} disabled={cargando || !datosTurno.fecha || !datosTurno.hora || !datosTurno.idMedico}>
                    {cargando ? 'Asignando...' : <><CheckCircleIcon fontSize="small" /> Confirmar Turno</>}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={onCancelar} disabled={cargando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
};

function GestorIngresoTramite({ tramite, onAvanzar }) {
    const [cargando, setCargando] = useState(false);
    const [mostrarFormularioTurno, setMostrarFormularioTurno] = useState(false);
    const [medicos, setMedicos] = useState([]);
    const [cargandoMedicos, setCargandoMedicos] = useState(true);
    const [lugares, setLugares] = useState([]);

    useEffect(() => {
        cargarMedicos();
        cargarLugares();
    }, []);

    const cargarMedicos = async () => {
        setCargandoMedicos(true);
        try {
            const medicosActivos = await listarMedicos(true); // Solo médicos activos
            setMedicos(medicosActivos);
        } catch (error) {
            console.error('Error al cargar médicos:', error);
            toast.error('Error al cargar la lista de médicos');
        } finally {
            setCargandoMedicos(false);
        }
    };

    const cargarLugares = async () => {
        try {
            const response = await DatosMaestrosService.getLugares();
            setLugares(response.data || []);
        } catch (error) {
            console.error('Error al cargar lugares:', error);
        }
    };

    const handleAsignarTurno = async (datosTurno) => {
        console.log('🚀 handleAsignarTurno iniciado');

        if (!datosTurno.fecha || !datosTurno.hora || !datosTurno.idMedico) {
            toast.error('Debe seleccionar fecha, hora y médico');
            return;
        }

        const fechaSeleccionada = parseLocalDate(datosTurno.fecha);
        if (!fechaSeleccionada) {
            toast.error('La fecha del turno es invalida');
            return;
        }

        setCargando(true);
        try {
            // 1️⃣ Crear el evento (turno médico)
            const medicoSeleccionado = medicos.find(m => m.idMedico === parseInt(datosTurno.idMedico));
            const nombreMedico = medicoSeleccionado ? `${medicoSeleccionado.nombre} ${medicoSeleccionado.apellido}` : 'No especificado';

            const eventoData = {
                titulo: `Turno Médico - ${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`,
                descripcion: `Médico: ${nombreMedico} - Lugar: ${datosTurno.lugar || 'No especificado'} - Observación: ${datosTurno.observaciones || 'No especificadas'}`,
                lugar: datosTurno.lugar || null,
                fecha: formatLocalFecha(datosTurno.fecha),
                horaInicio: datosTurno.hora,
                horaFin: calcularHoraFin(datosTurno.hora, 60),
                todoElDia: false,
                tipoEvento: 'Turno Médico',
                idTramite: tramite.idTramite,
                visibilidad: 'POR_AREA',
                canalesNotificacion: {
                    whatsapp: false,
                    email: false,
                    inapp: true
                }
            };

            // Crear evento con manejo robusto de errores
            try {
                await EventoService.crearEvento(eventoData);
                console.log('✅ Evento creado exitosamente');
            } catch (error) {
                console.error('❌ Error al crear evento:', error);
                throw error;
            }

            // Disparar evento personalizado para actualizar el calendario
            try {
                console.log('📅 GestorIngresoTramite ART: Evento creado - disparando actualización del calendario...');
                window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', {
                    detail: { timestamp: Date.now(), source: 'tramite' }
                }));
            } catch (error) {
                console.warn('⚠️ No se pudo disparar el evento personalizado:', error);
            }

            // 2️⃣ Cambiar el estado del trámite a "Turno médico asignado"
            console.log('🔄 Intentando cambiar estado del trámite...');
            console.log('idTramite:', tramite.idTramite);

            const estadoDestino = await obtenerEstadoARTPorNombre('Turno médico asignado');
            console.log('Respuesta de obtenerEstadoARTPorNombre:', estadoDestino);

            if (!estadoDestino) {
                throw new Error('No se pudo encontrar el estado "Turno médico asignado" en ámbito ART');
            }

            if (!estadoDestino.id && estadoDestino.id !== 0) {
                console.error('❌ Estado destino no tiene id válido:', estadoDestino);
                throw new Error(`Estado "Turno médico asignado" encontrado pero sin ID válido: ${JSON.stringify(estadoDestino)}`);
            }

            console.log('✅ Estado destino encontrado:', estadoDestino);

            const comentario = `Turno asignado\nFecha: ${formatearFecha(datosTurno.fecha)}\nHora: ${datosTurno.hora}\nMédico: ${nombreMedico}\nLugar: ${datosTurno.lugar || 'No especificado'}\nObservación: ${datosTurno.observaciones || 'No especificadas'}`;

            const payloadCambioEstado = {
                idEstado: estadoDestino.id,
                comentario: comentario
            };

            console.log('📤 Enviando payload de cambio de estado:', payloadCambioEstado);

            try {
                const respuestaCambioEstado = await cambioEstadoService.cambiarEstado(tramite.idTramite, payloadCambioEstado);
                console.log('✅ Estado cambiado exitosamente:', respuestaCambioEstado);
            } catch (errorCambioEstado) {
                console.error('❌ Error específico al cambiar estado:', errorCambioEstado);
                console.error('Error response:', errorCambioEstado.response?.data);
                console.error('Error status:', errorCambioEstado.response?.status);
                throw new Error(`Error al cambiar estado (${errorCambioEstado.response?.status}): ${errorCambioEstado.response?.data?.message || errorCambioEstado.message}`);
            }

            toast.success('Turno asignado y evento creado correctamente');
            setMostrarFormularioTurno(false);

            if (onAvanzar) {
                onAvanzar(); // Refresca la vista padre
            }
        } catch (error) {
            console.error('❌ Error al asignar turno:', error);
            console.error('Error details:', {
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                data: error?.response?.data,
                message: error?.message
            });
            const errorMessage = error?.response?.data?.mensaje || error?.response?.data?.message || error?.message || 'Error desconocido';
            toast.error(`Error al asignar el turno: ${errorMessage}`);
        } finally {
            setCargando(false);
        }
    };

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-primary mb-0">
                    Ingreso del Trámite (ART)
                </h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    Pendiente de Turno
                </span>
            </div>

            {!mostrarFormularioTurno && (
                <>
                    <div className="alert alert-info border-0 rounded-3">
                        <div className="d-flex align-items-start">
                            <div>
                                <strong>Siguiente Paso:</strong>
                                <p className="mb-0 mt-1">
                                    El trámite ha sido dado de alta correctamente. Debe asignar un turno médico
                                    para que el profesional evalúe los siniestros y lesiones del cliente.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-center mt-3">
                        <button
                            className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                            onClick={() => setMostrarFormularioTurno(true)}
                            disabled={cargando}
                        >
                            <>
                                <CalendarMonthIcon fontSize="small" />
                                Asignar Turno Médico
                            </>
                        </button>
                    </div>
                </>
            )}

            <FormularioTurnoART
                visible={mostrarFormularioTurno}
                onConfirmar={handleAsignarTurno}
                onCancelar={() => setMostrarFormularioTurno(false)}
                cargando={cargando}
                medicos={medicos}
                cargandoMedicos={cargandoMedicos}
                lugares={lugares}
            />
        </div>
    );
}

export default GestorIngresoTramite;